/**
 * useSpecsAdmin — данные и мутации «EO → Конфигуратор → Спецверсии» (A35):
 * staff-CRUD /api/quest-versions ПОВЕРХ клиентской special_versions (зона
 * Codex — клиентские пути не трогаем, удаление = archived). Образец —
 * useQuestsAdmin: optimistic-слой + run(fn, rollback) с flashOk/hint/reload.
 * Квесты филиала (кандидаты в базовые) — useAppData; справочник расходников
 * и цены — useQuestCats. Нормы расходников версии — quest_consumables с
 * version_id (A33); при смене базы (rebase) сначала чистится скоуп старого
 * квеста, иначе GET ?version= отдаст дубли из двух скоупов.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import {
  ApiException, type ApiList, type ApiOk, type Consumable, type Quest,
  type QuestConsumable, type QuestPrice, type QuestVersion,
} from "../api/types";
import { useAppData } from "./useAppData";
import { consWire, useQuestCats } from "./useQuestCats";
import type { ConsItem } from "./useQuestsAdmin";

/** Полный драфт сохранения спецверсии (кнопка «Сохранить»). */
export interface SpecSaveDraft {
  title: string;
  /** null = отвязать: сетки нет, бронирование недоступно (QS-1: сервер базу не требует). */
  slot_quest_id: string | null;
  duration_min: number;
  reset_min: number;
  /** Состав A31 «МЖН…»; '' = снять. */
  crew: string;
  client_visible: boolean;
  /** null = нормы не трогали (bulk не шлём, кроме rebase-переноса). */
  cons: ConsItem[] | null;
  pn: number; pb: number; pp: number;
}

export interface SpecsAdmin {
  /** Квесты филиала — кандидаты в базовые. */
  quests: Quest[];
  versions: QuestVersion[];
  consCat: Consumable[];
  prices: Record<string, QuestPrice>;
  /** Нормы расходников по версии (ленивый кеш, fetch на раскрытие карточки). */
  consVer: Record<string, QuestConsumable[]>;
  loadVersionCons: (versionId: string) => void;
  /** Нормы версии явным GET (кеш — фолбэк сети): клон без race прогрева. */
  versionConsFor: (versionId: string) => Promise<ConsItem[]>;
  /** Нормы базового квеста — prefill первой привязки (эталон prefillSpec js:47). */
  questConsFor: (questId: string) => Promise<ConsItem[]>;
  loading: boolean;
  hint: string | null;
  /** flashOk карточки: ключ `card:{id}` + счётчик n (React key хоста). */
  flash: { key: string; n: number } | null;
  createSpec: (d: { title: string; dur: number; reset: number; crew: string }) => Promise<string | null>;
  saveSpec: (v: QuestVersion, d: SpecSaveDraft) => Promise<boolean>;
  deleteSpec: (v: QuestVersion) => void;
  /** «Перенести в нормальные квесты» (21.08): версия → полноценный квест площадки базы, сама архивируется. */
  convertSpec: (v: QuestVersion) => void;
  refetch: () => void; // pull-to-refresh (A43.3): справочники + перезапрос норм
}

export function useSpecsAdmin(branchId: string): SpecsAdmin {
  const app = useAppData();
  const [localTick, setLocalTick] = useState(0);
  const { cats } = useQuestCats(branchId, localTick);
  const [vOver, setVOver] = useState<Record<string, Partial<QuestVersion>>>({});
  const [phantom, setPhantom] = useState<QuestVersion[]>([]);
  const [gone, setGone] = useState<string[]>([]);
  const [consVer, setConsVer] = useState<Record<string, QuestConsumable[]>>({});
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const pendingRef = useRef(0), flashT = useRef(0), branchRef = useRef(branchId);
  const reqVer = useRef(new Set<string>());
  const consVerRef = useRef(consVer);
  branchRef.current = branchId;
  consVerRef.current = consVer;

  const resetOver = () => { setVOver({}); setGone([]); };
  /* Смена филиала — оптимистика/кеш/hint/флэш неактуальны (useQuestsAdmin). */
  useEffect(() => {
    resetOver(); setPhantom([]); setConsVer({}); reqVer.current.clear();
    setHint(null); setFlash(null); window.clearTimeout(flashT.current);
  }, [branchId]);
  /* Свежие данные приехали и мутаций в полёте нет — оптимистика не нужна.
     Фантом здесь НЕ трогаем: реальная строка приезжает ДРУГИМ fetch'ем
     (cats.versions по localTick), app.reload может финишировать раньше —
     чистка по app.loading роняла созданную карточку до прихода строки. */
  useEffect(() => {
    if (!app.loading && pendingRef.current === 0) resetOver();
  }, [app.loading]);
  /* Фантом гаснет по факту прихода реальной строки в cats.versions. */
  useEffect(() => {
    setPhantom((p) => (p.some((x) => cats.versions.some((v) => v.id === x.id))
      ? p.filter((x) => !cats.versions.some((v) => v.id === x.id))
      : p));
  }, [cats.versions]);
  useEffect(() => () => window.clearTimeout(flashT.current), []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /** Обёртка мутации: flash при успехе, rollback+hint при ошибке. */
  const run = (fn: () => Promise<string | null>, rollback: () => void): Promise<boolean> => {
    setHint(null);
    const bid = branchRef.current;
    pendingRef.current += 1;
    return fn()
      .then((key) => {
        if (key && branchRef.current === bid) doFlash(key);
        return true;
      })
      .catch((err: unknown) => {
        rollback();
        if (branchRef.current === bid) setHint(err instanceof ApiException && err.status === 404
          ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
          : "Не сохранилось: ошибка сети. Значение возвращено.");
        return false;
      })
      .finally(() => {
        pendingRef.current -= 1;
        app.reload();
        setLocalTick((n) => n + 1);
      });
  };

  const versions = phantom
    .filter((p) => !cats.versions.some((v) => v.id === p.id))
    .concat(cats.versions)
    .filter((v) => !gone.includes(v.id))
    .map((v) => (vOver[v.id] ? { ...v, ...vOver[v.id] } : v));

  const quests = app.quests
    .filter((q) => q.branch_id === branchId)
    .sort((a, z) => (a.sort_order ?? 0) - (z.sort_order ?? 0));

  const loadVersionCons = useCallback((versionId: string) => {
    if (reqVer.current.has(versionId)) return;
    reqVer.current.add(versionId);
    void api<ApiList<QuestConsumable>>(`/api/quest-consumables?version=${encodeURIComponent(versionId)}`)
      .then((r) => setConsVer((m) => ({ ...m, [versionId]: r.results ?? [] })))
      .catch(() => { reqVer.current.delete(versionId); });
  }, []);

  const versionConsFor = useCallback((versionId: string): Promise<ConsItem[]> =>
    api<ApiList<QuestConsumable>>(`/api/quest-consumables?version=${encodeURIComponent(versionId)}`)
      .then((r) => consWire(r.results ?? []))
      .catch(() => consWire(consVerRef.current[versionId] ?? [])), []);

  const questConsFor = useCallback((questId: string): Promise<ConsItem[]> =>
    api<ApiList<QuestConsumable>>(`/api/quest-consumables?quest=${encodeURIComponent(questId)}`)
      .then((r) => (r.results ?? []).map((c) => ({ consumable_id: c.consumable_id, qty: c.qty, per_player: c.per_player })))
      .catch(() => []), []);

  const createSpec = async (d: { title: string; dur: number; reset: number; crew: string }) => {
    let newId: string | null = null;
    const tmp = `v_${Date.now().toString(36)}`;
    setPhantom((p) => [{
      id: tmp, title: d.title, slot_quest_id: null, duration_min: d.dur, reset_min: d.reset,
      operators_comp: d.crew || null, client_visible: 0,
      base_players: null, base_price: null, price_per_extra: null,
    }, ...p]);
    const ok = await run(async () => {
      const body: Record<string, unknown> = { title: d.title, duration_min: d.dur, reset_min: d.reset };
      if (d.crew) body.operators_comp = d.crew;
      const r = await api<ApiOk>("/api/quest-versions", { method: "POST", body });
      newId = typeof r.id === "string" ? r.id : null;
      /* id даёт сервер: переименовать фантом, чтобы dedupe-фильтр совпал. */
      const real = newId;
      if (real) setPhantom((p) => p.map((x) => (x.id === tmp ? { ...x, id: real } : x)));
      return newId ? `card:${newId}` : null;
    }, () => setPhantom((p) => p.filter((x) => x.id !== tmp)));
    return ok ? newId : null;
  };

  const saveSpec = (v: QuestVersion, d: SpecSaveDraft): Promise<boolean> => {
    const nb = d.slot_quest_id;
    const bq = nb ? app.quests.find((x) => x.id === nb) : undefined;
    const patch: Partial<QuestVersion> = {
      title: d.title, slot_quest_id: nb, duration_min: d.duration_min,
      reset_min: d.reset_min, operators_comp: d.crew || null,
      client_visible: d.client_visible ? 1 : 0, base_players: d.pn,
      base_price: d.pb, price_per_extra: d.pp, base_short: bq?.short_name ?? null,
      base_duration: bq?.duration ?? null, base_reset: bq?.reset_time ?? null,
      base_branch_id: bq?.branch_id ?? null,
    };
    setVOver((o) => ({ ...o, [v.id]: patch }));
    return run(async () => {
      await api<ApiOk>(`/api/quest-versions/${v.id}`, { method: "PUT", body: {
        title: d.title, slot_quest_id: nb, duration_min: d.duration_min,
        reset_min: d.reset_min, operators_comp: d.crew,
        client_visible: d.client_visible ? 1 : 0,
        base_players: d.pn, base_price: d.pb, price_per_extra: d.pp,
      } });
      const rebased = (v.slot_quest_id ?? null) !== nb;
      const items = d.cons
        ?? (consVer[v.id] ?? []).map((c) => ({ consumable_id: c.consumable_id, qty: c.qty, per_player: c.per_player }));
      if (rebased && v.slot_quest_id) {
        await api<ApiOk>("/api/quest-consumables/bulk", { method: "PUT", body: { quest_id: v.slot_quest_id, version_id: v.id, items: [] } });
      }
      if (nb && (d.cons !== null || (rebased && items.length))) {
        await api<ApiOk>("/api/quest-consumables/bulk", { method: "PUT", body: { quest_id: nb, version_id: v.id, items } });
      }
      setConsVer((m) => ({ ...m, [v.id]: nb
        ? items.map((c) => ({ quest_id: nb, version_id: v.id, ...c }))
        : [] }));
      return `card:${v.id}`;
    }, () => setVOver((o) => {
      if (o[v.id] !== patch) return o; // поверх уже более новый драфт
      const n = { ...o }; delete n[v.id]; return n;
    }));
  };

  const deleteSpec = (v: QuestVersion) => {
    setGone((g) => [...g, v.id]);
    void run(async () => {
      await api<ApiOk>(`/api/quest-versions/${v.id}`, { method: "DELETE" });
      return null;
    }, () => setGone((g) => g.filter((x) => x !== v.id)));
  };

  const convertSpec = (v: QuestVersion) => {
    setGone((g) => [...g, v.id]);
    void run(async () => {
      await api<ApiOk>(`/api/quest-versions/${v.id}/convert-to-quest`, { method: "POST" });
      app.reload(); // новый квест должен появиться в списке квестов
      return null;
    }, () => setGone((g) => g.filter((x) => x !== v.id)));
  };

  return {
    quests, versions, consCat: cats.cons, prices: cats.prices,
    consVer, loadVersionCons, versionConsFor, questConsFor,
    loading: (app.loading && app.quests.length === 0) || (!!branchId && !cats.loaded),
    hint, flash, createSpec, saveSpec, deleteSpec, convertSpec,
    /* Pull-to-refresh: справочники + перезапрос уже загруженных норм —
       loadVersionCons-эффект карточки сам не перезапустится. */
    refetch: () => {
      const ids = [...reqVer.current];
      reqVer.current.clear();
      for (const id of ids) loadVersionCons(id);
      setLocalTick((n) => n + 1);
    },
  };
}
